import { useEffect, useState } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { api } from '../api/client.js';
import { clientResetCredential } from '../crypto/session.js';
import { Alert } from '../components/ui.js';
import { PasswordStrength } from '../components/PasswordStrength.js';

export function VerifyEmail({ mode = 'verify' }: { mode?: 'verify' | 'reset' }) {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const location = useLocation();
  const emailHint = (location.state as { email?: string } | null)?.email;

  const [status, setStatus] = useState<'idle' | 'working' | 'done' | 'error'>('idle');
  const [error, setError] = useState<string | null>(null);

  // Reset mode fields:
  const [newPassword, setNewPassword] = useState('');
  const [confirm, setConfirm] = useState('');

  useEffect(() => {
    if (mode === 'verify' && token) verify(token);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, mode]);

  async function verify(t: string) {
    setStatus('working');
    try {
      await api.post('/auth/verify-email', { token: t });
      setStatus('done');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Verification failed');
      setStatus('error');
    }
  }

  async function doReset(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (newPassword.length < 12) return setError('Master password must be at least 12 characters.');
    if (newPassword !== confirm) return setError('Passwords do not match.');
    setStatus('working');
    try {
      const cred = await clientResetCredential(newPassword);
      await api.post('/auth/reset-password', { token, ...cred });
      setStatus('done');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Reset failed');
      setStatus('error');
    }
  }

  if (mode === 'reset') {
    if (status === 'done') {
      return <ResultCard title="Password reset" body="Your login credential has been updated. Sign in with your new master password." cta="/login" ctaLabel="Sign in" />;
    }
    return (
      <div className="auth-wrap">
        <div className="panel auth-card">
          <h2 style={{ margin: '0 0 6px' }}>Reset master password</h2>
          <Alert kind="warn">
            Zero-knowledge note: resetting creates fresh encryption keys. Any previous vault items are
            unreadable unless you previously enrolled a recovery key. There is no other recovery path.
          </Alert>
          {error && <Alert kind="error">{error}</Alert>}
          <form onSubmit={doReset}>
            <div className="field">
              <label>New master password</label>
              <input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} required />
              {newPassword && <div className="mt-sm"><PasswordStrength password={newPassword} /></div>}
            </div>
            <div className="field">
              <label>Confirm</label>
              <input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required />
            </div>
            <button className="btn full" disabled={status === 'working'}>Reset password</button>
          </form>
        </div>
      </div>
    );
  }

  // Email verification mode
  if (status === 'done') {
    return <ResultCard title="Email verified 🎉" body="Your email is confirmed. You can now sign in and unlock your vault." cta="/login" ctaLabel="Sign in" />;
  }
  if (status === 'error') {
    return (
      <div className="auth-wrap">
        <div className="panel auth-card">
          <h2 style={{ margin: '0 0 6px' }}>Verification problem</h2>
          <Alert kind="error">{error ?? 'Invalid or expired link.'}</Alert>
          <Link className="btn full" to="/register">Try again</Link>
        </div>
      </div>
    );
  }
  return (
    <div className="auth-wrap">
      <div className="panel auth-card center">
        <div className="mono muted">{status === 'working' ? 'Verifying…' : 'Check your email'}</div>
        {emailHint && <p className="muted">We sent a verification link to {emailHint}.</p>}
        <p className="muted" style={{ fontSize: 13 }}>Click the link in the email, or paste a token below.</p>
        <ManualToken kind="verify" />
        <Link className="btn secondary sm mt" to="/login">Back to sign in</Link>
      </div>
    </div>
  );
}

function ManualToken({ kind }: { kind: 'verify' | 'reset' }) {
  const [t, setT] = useState('');
  const go = () => { if (t) window.location.href = `/${kind === 'verify' ? 'verify-email' : 'reset-password'}?token=${encodeURIComponent(t)}`; };
  return (
    <div className="flex mt" style={{ gap: 8 }}>
      <input placeholder="paste token" value={t} onChange={(e) => setT(e.target.value)} />
      <button className="btn secondary sm" onClick={go}>Go</button>
    </div>
  );
}

function ResultCard({ title, body, cta, ctaLabel }: { title: string; body: string; cta: string; ctaLabel: string }) {
  return (
    <div className="auth-wrap">
      <div className="panel auth-card center">
        <h2 style={{ marginTop: 0 }}>{title}</h2>
        <p className="muted">{body}</p>
        <Link className="btn full mt" to={cta}>{ctaLabel}</Link>
      </div>
    </div>
  );
}
